import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, BellRing, CalendarDays, FolderLock, Users, RefreshCw, ArrowLeftRight, BarChart3, Bell,
  Settings, Shield, Search, Plus, Home, MoreHorizontal, LogOut, Cloud,
} from "lucide-react";
import { Wordmark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtBytes } from "@/lib/brand";
import { VoiceButton } from "@/components/VoiceReminder";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/reminders", label: "Reminders", icon: BellRing },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/documents", label: "Documents", icon: FolderLock },
  { to: "/people-assets", label: "People & Assets", icon: Users },
  { to: "/renewals", label: "Renewals", icon: RefreshCw },
  { to: "/import-export", label: "Import & Export", icon: ArrowLeftRight },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/settings", label: "Settings", icon: Settings },
];

function StorageMeter() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/attachments", { params: { q: "__none__" } }).then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;
  const pct = Math.min(100, (s.storage_used / s.storage_quota) * 100);
  return (
    <div className="rounded-2xl border border-[var(--pm-border)] p-4 bg-gradient-to-br from-white to-[#FFF4F8]" data-testid="storage-meter">
      <p className="text-sm font-bold flex items-center gap-2"><Cloud className="w-4 h-4" /> Document storage</p>
      <div className="h-2 rounded-full bg-[#ECE9F5] mt-3 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.max(pct, 2)}%`, background: "var(--pm-gradient)" }} /></div>
      <p className="text-xs text-[var(--pm-muted)] mt-2 flex justify-between"><span>{fmtBytes(s.storage_used)} of {fmtBytes(s.storage_quota)}</span><Link to="/documents" className="text-[var(--pm-primary)] font-semibold">Manage</Link></p>
    </div>
  );
}

function SearchBox() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  const ref = useRef();
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then(({ data }) => setRes(data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const k = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); ref.current?.focus(); } };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  const go = (path) => { setQ(""); setRes(null); nav(path); };
  const empty = res && !res.reminders.length && !res.entities.length && !res.documents.length;
  return (
    <div className="relative w-full max-w-md">
      <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pm-muted)]" />
      <input ref={ref} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reminders, documents, people…  ⌘K"
        className="pm-input pl-10 bg-white" data-testid="global-search-input" aria-label="Search" />
      {res && (
        <div className="absolute z-50 mt-2 w-full pm-card p-2 max-h-80 overflow-auto" data-testid="global-search-results">
          {empty && <p className="text-sm text-[var(--pm-muted)] p-3">No matches for “{q}”.</p>}
          {res.reminders.map((r) => <button key={r.id} onClick={() => go(`/reminders/${r.id}`)} className="w-full text-left p-2.5 rounded-lg hover:bg-[#F5F2FF]"><p className="text-sm font-semibold">{r.title}</p><p className="text-xs text-[var(--pm-muted)]">{r.sub}</p></button>)}
          {res.entities.map((e) => <button key={e.id} onClick={() => go(`/reminders?owner_id=${e.id}`)} className="w-full text-left p-2.5 rounded-lg hover:bg-[#F5F2FF]"><p className="text-sm font-semibold">{e.name}</p><p className="text-xs text-[var(--pm-muted)] capitalize">{e.kind}</p></button>)}
          {res.documents.map((d) => <button key={d.id} onClick={() => go(`/reminders/${d.reminder_id}`)} className="w-full text-left p-2.5 rounded-lg hover:bg-[#F5F2FF]"><p className="text-sm font-semibold">{d.name}</p><p className="text-xs text-[var(--pm-muted)]">Document</p></button>)}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name || user?.email || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="w-10 h-10 rounded-full bg-[#EFEBFF] text-[var(--pm-primary)] font-bold text-sm grid place-items-center overflow-hidden" data-testid="user-menu-trigger">
        {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" /> : initials}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel><p className="font-semibold">{user?.name}</p><p className="text-xs text-[var(--pm-muted)] font-normal truncate">{user?.email}</p></DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => nav("/settings")} data-testid="user-menu-settings">Settings</DropdownMenuItem>
        {user?.is_admin && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="user-menu-admin">Admin</DropdownMenuItem>}
        <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="user-menu-logout"><LogOut className="w-4 h-4 mr-2" />Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileNav() {
  const [more, setMore] = useState(false);
  const items = [{ to: "/dashboard", label: "Home", icon: Home }, { to: "/calendar", label: "Calendar", icon: CalendarDays }];
  const items2 = [{ to: "/documents", label: "Documents", icon: FolderLock }];
  const link = (n) => (
    <NavLink key={n.to} to={n.to} className={({ isActive }) => `flex flex-col items-center gap-1 text-[0.68rem] font-semibold ${isActive ? "text-[var(--pm-primary)]" : "text-[var(--pm-muted)]"}`} data-testid={`mobile-nav-${n.label.toLowerCase()}`}>
      <n.icon className="w-5 h-5" />{n.label}
    </NavLink>
  );
  return (
    <>
      {more && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/20" onClick={() => setMore(false)}>
          <div className="absolute bottom-20 left-3 right-3 pm-card p-3 grid grid-cols-3 gap-2" data-testid="mobile-more-menu">
            {NAV.slice(1).map((n) => (
              <NavLink key={n.to} to={n.to} className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-[#F5F2FF] text-xs font-semibold text-center"><n.icon className="w-5 h-5 text-[var(--pm-primary)]" />{n.label}</NavLink>
            ))}
          </div>
        </div>
      )}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[var(--pm-border)] h-[68px] grid grid-cols-5 items-center no-print" aria-label="Mobile">
        {items.map(link)}
        <Link to="/reminders/new" className="mx-auto -mt-8 w-14 h-14 rounded-full grid place-items-center text-white shadow-[0_12px_24px_-8px_rgba(195,0,119,.7)]" style={{ background: "var(--pm-gradient)" }} aria-label="Add reminder" data-testid="mobile-nav-add">
          <Plus className="w-6 h-6" />
        </Link>
        {items2.map(link)}
        <button onClick={() => setMore(!more)} className="flex flex-col items-center gap-1 text-[0.68rem] font-semibold text-[var(--pm-muted)]" data-testid="mobile-nav-more"><MoreHorizontal className="w-5 h-5" />More</button>
      </nav>
    </>
  );
}

export default function AppLayout({ children }) {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[var(--pm-bg)]">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[244px] bg-white border-r border-[var(--pm-border)] flex-col p-4 no-print" data-testid="app-sidebar">
        <div className="px-2 py-3 mb-4"><Wordmark to="/dashboard" testid="sidebar-wordmark" /></div>
        <nav className="flex flex-col gap-1 flex-1 overflow-y-auto" aria-label="App">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className="pm-nav-link" data-testid={`sidebar-${n.to.slice(1)}`}><n.icon className="w-[18px] h-[18px]" />{n.label}</NavLink>
          ))}
          {user?.is_admin && <NavLink to="/admin" className="pm-nav-link" data-testid="sidebar-admin"><Shield className="w-[18px] h-[18px]" />Admin</NavLink>}
        </nav>
        <StorageMeter />
      </aside>
      <div className="lg:pl-[244px]">
        <header className="sticky top-0 z-30 bg-[var(--pm-bg)]/85 backdrop-blur-xl no-print">
          <div className="px-4 sm:px-6 lg:px-8 h-[76px] flex items-center gap-4 justify-between">
            <div className="lg:hidden"><Link to="/dashboard"><img src="/brand/icon-128.png" alt="PinMyDates" className="w-10 h-10 rounded-xl" /></Link></div>
            <div className="flex-1 flex"><SearchBox /></div>
            <div className="flex items-center gap-3">
              <VoiceButton variant="icon" testid="topbar-voice-btn" />
              <Link to="/notifications" className="w-10 h-10 rounded-full grid place-items-center hover:bg-white" aria-label="Notifications" data-testid="topbar-notifications"><Bell className="w-5 h-5" /></Link>
              <UserMenu />
            </div>
          </div>
        </header>
        <main className="px-4 sm:px-6 lg:px-8 pb-28 lg:pb-12 pt-2 max-w-[1500px]">{children}</main>
      </div>
      <MobileNav />
    </div>
  );
}
